import { Route, Routes, useNavigate, Navigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  MainPage,
  MyAccountPage,
  GVLoginErrorPage,
  LinkAccountPage,
  LicenseAgreementPage,
  SessionPage,
  InternalPage,
  AutomationPage,
  AuditExportDownloadPage,
} from 'pages';
import { PageLoader, AuthenticationGuard, InternalGuard } from 'components';
import { useTracker } from 'components/Tracker/TrackerProvider';
import { useEffect, useMemo, useRef } from 'react';
import { useUserStateStore } from 'zstore/userStateStore';
import { getAppMetadata, getValidationError } from 'utils/auth';
import { getUserMetadata } from 'store/myAccount/requests';
import { getDisplaySettings } from 'store/app/displaySettingsRequest';
import { getTrackerMetadata } from 'store/trackerMetadata/trackerMetadata';
import { app } from 'store';
import { useDispatch } from 'react-redux';
import CloningProgressToast from 'components/modals/CloningProgressToast';
import store from 'store/store';
import LDClientSingleton from 'utils/getFlagValue/ldClientSingleton';
import * as Sentry from '@sentry/react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { expandInspectionUrl } from 'utils/shortenUrl';
import InspectionAccessDenied from 'components/GVLoginErrorPage/InspectionAccessDenied';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const ShortUrlRedirect = () => {
  const { shortId } = useParams();

  if (!shortId) {
    return <Navigate to="/" replace />;
  }

  const fullUrl = expandInspectionUrl(`/s/${shortId}`);
  if (fullUrl === `/s/${shortId}`) {
    return <Navigate to="/" replace />; // Invalid short URL
  }

  // Redirect to the expanded URL
  return <Navigate to={fullUrl} replace />;
};

export const App = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect } = useAuth0();
  const { setUser, user: storeUser } = useUserStateStore();
  const { track } = useTracker();
  const loginProcessedRef = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ldClient = useLDClient();
  const featureFlags = useFlags();

  if (ldClient) {
    LDClientSingleton.setInstance(ldClient);
  }

  // Sentry.init() runs at module load, before LaunchDarkly has any flag values, so session replay
  // is attached here rather than in its `integrations` array. Sentry ignores a repeat call.
  useEffect(() => {
    if (featureFlags.sentryReplayIntegration) {
      Sentry.addIntegration(
        Sentry.replayIntegration({ maskAllInputs: false, maskAllText: false, blockAllMedia: false }),
      );
    }
  }, [featureFlags.sentryReplayIntegration]);

  const appMetadata = useMemo(() => getAppMetadata(storeUser), [storeUser.sub]);
  const validationError = useMemo(() => getValidationError(storeUser), [storeUser.sub]);

  // Hide MUI tooltips on scroll to prevent them from floating outside their context
  // Target specifically the MUI tooltip content class, not any parent Popper
  useEffect(() => {
    let pendingFrame: number | null = null;

    const hideTooltipsOnScroll = () => {
      if (pendingFrame) return;

      pendingFrame = requestAnimationFrame(() => {
        // Only target actual MUI Tooltip content elements
        document.querySelectorAll<HTMLElement>('.MuiTooltip-tooltip').forEach((tooltip) => {
          tooltip.style.visibility = 'hidden';
        });
        pendingFrame = null;
      });
    };

    window.addEventListener('scroll', hideTooltipsOnScroll, true);
    return () => {
      window.removeEventListener('scroll', hideTooltipsOnScroll, true);
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
    };
  }, []);

  // User is already authenticated - get claims and set user in store
  useEffect(() => {
    if (isAuthenticated && !storeUser.sub && !loginProcessedRef.current) {
      getAccessTokenSilently()
        .then((token) => {
          if (token) {
            window.accessToken = token; // @ugly hack to make axios interceptors work
            getIdTokenClaims().then((claims) => {
              const fullUser = {
                ...user,
                ...claims,
              };
              try {
                // The identity used for login has an access_token property.
                const identityUsedForLogin = fullUser.identities?.find((i: any) => 'access_token' in i);

                // If we can't determine from identities, fallback to checking the primary identity from sub.
                const isSSO = identityUsedForLogin
                  ? identityUsedForLogin.provider !== 'auth0'
                  : !fullUser.sub?.startsWith('auth0|');

                track({
                  name: isSSO ? 'user-login-sso-success-client' : 'user-login-non-sso-success-client',
                  data: {
                    actingUserEmail: fullUser.email,
                  },
                  orgId: fullUser[`https://${import.meta.env.VITE_AUTH0_DOMAIN}_app_metadata`]?.org_id,
                  userId: fullUser.sub,
                });
              } catch (e) {
                console.error('Error tracking user login', e);
              }

              try {
                const ldUserContext = {
                  kind: 'user',
                  key: fullUser.sub,
                  custom: {
                    userId: fullUser.sub,
                  },
                };

                // Define the organization context
                const ldOrgContext = {
                  kind: 'organization',
                  key: fullUser[`https://${import.meta.env.VITE_AUTH0_DOMAIN}_app_metadata`]?.org_id
                    ? fullUser[`https://${import.meta.env.VITE_AUTH0_DOMAIN}_app_metadata`]?.org_id
                    : 'NoOrg', // Unique identifier for the organization
                  custom: {
                    orgId: fullUser[`https://${import.meta.env.VITE_AUTH0_DOMAIN}_app_metadata`]?.org_id
                      ? fullUser[`https://${import.meta.env.VITE_AUTH0_DOMAIN}_app_metadata`]?.org_id
                      : 'NoOrg',
                  },
                };

                if (ldOrgContext && ldUserContext) {
                  const multiContext = {
                    kind: 'multi',
                    user: ldUserContext,
                    organization: ldOrgContext,
                  };

                  // Identify user with launchdarkly
                  ldClient?.identify(multiContext);
                }
              } catch (e) {
                console.error('Error identifying user with launchdarkly', e);
              }

              // Set user along with claims so that it can be the source of truth throughout the app
              setUser(fullUser);
              // Dispatch user display name after setting the user and using fullUser details
              dispatch(app.actions.setDisplayName(fullUser.name || fullUser.email || ''));
              Sentry.setUser({
                id: fullUser.sub,
                email: fullUser.email,
              });

              Sentry.setTag(
                'orgId',
                fullUser[`https://${import.meta.env.VITE_AUTH0_DOMAIN}_app_metadata`]?.org_id || '',
              );
              loginProcessedRef.current = true;
            });
          }
        })
        .catch((error) => {
          track({
            name: 'user-login-attempt-failed',
            data: {
              error: error.message,
              returnTo: window.location.pathname,
            },
          });
          loginWithRedirect({
            appState: { targetUrl: window.location.pathname },
          });
        });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (storeUser.sub) {
      store.dispatch(getUserMetadata());
      store.dispatch(getDisplaySettings());
    }
  }, [storeUser.sub]);

  // The user is authenticated but requires link, redirect to the link page
  useEffect(() => {
    if (appMetadata?.requireLink) {
      navigate('/link-accounts');
    }
  }, [appMetadata?.requireLink]);

  // There is an error with validation of the user or auth0 - redirect to error page to deny access
  useEffect(() => {
    const url = window.location.href; // @TODO MAYBE USE REACT ROUTER :P
    const isAuth0InternalError = url.indexOf('error_description=Internal') !== -1;

    if (validationError || (url.match('error') && !isAuth0InternalError)) {
      navigate('/error');
    }
  }, [validationError]);

  // Auth0 is loading, show page loading screen
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SentryRoutes>
        <Route path="/" element={<AuthenticationGuard component={MainPage} />}>
          <Route index element={<SessionPage />} />
          <Route path="s/:shortId" element={<ShortUrlRedirect />} />
          <Route path="inspection/:id/:type?" element={<SessionPage />} />
          <Route path="agreement" element={<LicenseAgreementPage />} />
          <Route path="audit/exports/:id/download" element={<AuditExportDownloadPage />} />
          <Route path="myaccount/*" element={<MyAccountPage />} />
          <Route path="internal" element={<InternalGuard component={<InternalPage />} />} />
          {featureFlags.enableAutomationSection && <Route path="automation" element={<AutomationPage />} />}
        </Route>
        <Route path="error" element={<GVLoginErrorPage />} />
        <Route path="inspection-access-denied" element={<InspectionAccessDenied />} />
        <Route path="link-accounts" element={<AuthenticationGuard component={LinkAccountPage} />} />
        <Route path="*" element={<GVLoginErrorPage />} /> {/* @TODO we need a NotFoundPage design */}
      </SentryRoutes>
      <CloningProgressToast />
    </>
  );
};
